import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router";
import { errorMessage, patch, post } from "../api";
import { Alert, Button, Loading, PageHeader, Panel, PanelHeader, SelectField } from "../components/ui";
import { BookingTab } from "./settings/BookingTab";
import { MarketingTab } from "./settings/MarketingTab";
import { AnnounceTab } from "./settings/AnnounceTab";
import { ClinicTab } from "./settings/ClinicTab";
import { DevicesTab } from "./settings/DevicesTab";
import { useSettings, type SettingsResponse } from "./settings/common";
import { SETTINGS_GROUPS } from "../components/SettingsLayout";
import { FormsTab } from "./settings/FormsTab";
import { LocationsTab } from "./settings/LocationsTab";
import { GfeTab } from "./settings/GfeTab";
import { MessagesTab } from "./settings/MessagesTab";
import { MobileTab } from "./settings/MobileTab";
import { PhoneTab } from "./settings/PhoneTab";
import { PhoneAssistantTab } from "./settings/PhoneAssistantTab";
import { ResourcesTab } from "./settings/ResourcesTab";
import { TagsTab } from "./settings/TagsTab";
import { SetupTab } from "./settings/SetupTab";

const TIMEOUTS = [15, 20, 30, 45, 60, 120, 240, 480, 720];
const timeoutLabel = (t: number) => (t < 60 ? `${t} minutes` : t === 60 ? "1 hour" : `${t / 60} hours`);

const TABS = SETTINGS_GROUPS.flatMap((g) => g.items).filter((i) => i.to.startsWith("/settings/"));

export function SettingsPage() {
  const { tab } = useParams();
  const data = useSettings();
  if (!tab || !TABS.some((t) => t.to.endsWith(`/${tab}`))) return <Navigate to="/settings/clinic" replace />;

  return (
    <div className="max-w-3xl">
      <PageHeader title={TABS.find((t) => t.to.endsWith(`/${tab}`))!.label} description="These apply to everyone at your clinic." />
      {data.isLoading ? (
        <Loading />
      ) : data.error || !data.data ? (
        <Alert>{errorMessage(data.error)}</Alert>
      ) : tab === "clinic" ? (
        <ClinicTab data={data.data} />
      ) : tab === "locations" ? (
        <LocationsTab />
      ) : tab === "resources" ? (
        <ResourcesTab />
      ) : tab === "tags" ? (
        <TagsTab />
      ) : tab === "booking" ? (
        <BookingTab data={data.data} />
      ) : tab === "mobile" ? (
        <MobileTab data={data.data} />
      ) : tab === "forms" ? (
        <FormsTab data={data.data} />
      ) : tab === "messages" ? (
        <MessagesTab data={data.data} />
      ) : tab === "gfe" ? (
        <GfeTab data={data.data} />
      ) : tab === "phone" ? (
        <PhoneTab />
      ) : tab === "phone-assistant" ? (
        <PhoneAssistantTab />
      ) : tab === "setup" ? (
        <SetupTab />

      ) : tab === "marketing" ? (
        <MarketingTab />
      ) : tab === "announcements" ? (
        <AnnounceTab />
      ) : tab === "devices" ? (
        <DevicesTab data={data.data} />
      ) : (
        <BrandingSecurityTab data={data.data} />
      )}
    </div>
  );
}

function BrandingSecurityTab({ data: settings }: { data: SettingsResponse }) {
  const qc = useQueryClient();
  const [timeout, setTimeoutValue] = useState(30);
  const [colors, setColors] = useState({ primary: "#1F1A1C", accent: "#C51A6A" });
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const d = settings;
    setTimeoutValue(d.settings["security.inactivityTimeoutMinutes"]);
    setColors({ primary: d.settings["branding.primaryColor"], accent: d.settings["branding.accentColor"] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const refresh = async (msg: string) => {
    await qc.invalidateQueries({ queryKey: ["settings"] });
    await qc.invalidateQueries({ queryKey: ["me"] });
    setNotice(msg);
  };

  const saveSecurity = useMutation({
    mutationFn: () => patch("/api/settings", { "security.inactivityTimeoutMinutes": timeout }),
    onSuccess: () => refresh("Security settings saved. The new timeout applies to everyone now."),
  });
  const saveBranding = useMutation({
    mutationFn: () => patch("/api/settings", { "branding.primaryColor": colors.primary, "branding.accentColor": colors.accent }),
    onSuccess: () => refresh("Branding saved."),
  });
  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > 2 * 1024 * 1024) throw new Error("Logo must be under 2 MB.");
      const up = await post<{ key: string; url: string; headers: Record<string, string> }>("/api/settings/logo-upload", { contentType: file.type });
      const res = await fetch(up.url, { method: "PUT", headers: up.headers, body: file });
      if (!res.ok) throw new Error("The logo couldn't be uploaded. Try again.");
      await patch("/api/settings", { "branding.logoKey": up.key });
    },
    onSuccess: () => refresh("Logo updated."),
  });

  const error = saveSecurity.error ?? saveBranding.error ?? uploadLogo.error;

  return (
    <div>
      <div className="mb-4 flex flex-col gap-2">
        {notice && <Alert tone="ok">{notice}</Alert>}
        {error && <Alert>{errorMessage(error)}</Alert>}
      </div>

      <div className="flex flex-col gap-6">
        <Panel>
          <PanelHeader title="Security" description="Staff are signed out automatically after this much inactivity (and always after 12 hours). Shorter is safer on shared devices." />
          <form
            className="flex flex-col gap-4 px-5 py-5"
            onSubmit={(e) => {
              e.preventDefault();
              setNotice(null);
              saveSecurity.mutate();
            }}
          >
            <SelectField label="Sign out after" value={timeout} onChange={(e) => setTimeoutValue(Number(e.target.value))}>
              {TIMEOUTS.map((t) => (
                <option key={t} value={t}>
                  {timeoutLabel(t)} of inactivity
                </option>
              ))}
            </SelectField>
            <div className="flex justify-end">
              <Button variant="primary" type="submit" busy={saveSecurity.isPending}>
                Save security settings
              </Button>
            </div>
          </form>
        </Panel>

        <Panel>
          <PanelHeader title="Branding" description="Your logo and colors. Booking pages will use these later." />
          <div className="flex flex-col gap-6 px-5 py-5">
            <div className="flex flex-wrap items-center gap-5">
              <div className="grid size-20 place-items-center overflow-hidden rounded-[var(--radius-panel)] border border-line bg-white">
                {settings.logoUrl ? <img src={settings.logoUrl} alt="Clinic logo" className="max-h-full max-w-full object-contain p-2" /> : <span className="text-xs text-muted">No logo</span>}
              </div>
              <label className="cursor-pointer">
                <span className="inline-flex items-center rounded-[var(--radius-control)] border border-line bg-white px-4 py-2 text-sm font-semibold hover:border-ink-soft">
                  {uploadLogo.isPending ? "Uploading…" : settings.logoUrl ? "Replace logo" : "Upload logo"}
                </span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="sr-only"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setNotice(null);
                      uploadLogo.mutate(f);
                    }
                    e.target.value = "";
                  }}
                />
              </label>
              <p className="text-sm text-muted">PNG, JPG, SVG, or WebP, up to 2 MB.</p>
            </div>
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                setNotice(null);
                saveBranding.mutate();
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <ColorField label="Main color" value={colors.primary} onChange={(v) => setColors({ ...colors, primary: v })} />
                <ColorField label="Accent color" value={colors.accent} onChange={(v) => setColors({ ...colors, accent: v })} />
              </div>
              <div className="flex justify-end">
                <Button variant="primary" type="submit" busy={saveBranding.isPending}>
                  Save branding
                </Button>
              </div>
            </form>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <span className="mb-1 block text-sm font-semibold text-ink-soft">{label}</span>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-10 w-12 cursor-pointer rounded-md border border-line bg-white p-1" aria-label={`${label} picker`} />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          pattern="#[0-9a-fA-F]{6}"
          className="w-32 rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 uppercase"
          aria-label={`${label} hex value`}
        />
      </div>
    </div>
  );
}
